/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Button, Alert } from 'react-bootstrap';
import moment from "moment/moment";
import Spinner from "react-bootstrap/Spinner";

// Constants & Common
import { APP_COLORS } from '../../common/Constants';
import Modal from "../../common/components/Modal";
import Sidebar from "../../common/components/Sidebar";
import SideBarContent from "../../common/components/SidebarContent";

// Components
import ExploreMapContainer from './ExploreMapContainer';
import ExploreCards from "./components/cards/ExploreCards";
import FilterSidebar from "./filters/FilterSidebar";

// Modals
import UploadListDialog from "./upload/UploadListDialog";
import SaveModal from './SaveModal';
import ExportModal from "./ExportModal";
import ExploreToolbar from "./ExploreToolbar";
import {useSelector} from "react-redux";
import {resolveUniverseConfig} from "./universeQuerySlice";

import './Explore.scss';

const Explore = (props) => {
    const {
        currentUniverse,
        totalContacts,
        totalCompanies,
        loading,
        hasFilter,
        canExport,
        accuracyData,
        toggleMap,
        onToggleMapChange,
        handleClearAllFilter,
        handleSearch,
        handleToggleUniverse, // If needed
        handleOnSelectSearchCriteria,
        handleSettingAccuracy
    } = props;

    const universeState = useSelector(state => state.universe);

    const currentUniverseData = universeState.universes[currentUniverse];
    const exploreByContact = currentUniverseData?.exploreByContact ?? false;

    // const [toggleMap, setToggleMap] = useState(true);
    const [expanded, setExpanded] = useState(true);

    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showSaveSearchDialog, setShowSaveSearchDialog] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    // 'hidden' | 'in' (visible) | 'out' (playing fade-to-the-right exit)
    const [exportFlash, setExportFlash] = useState('hidden');
    const [showApplySearchConfirmModal, setShowApplySearchConfirmModal] = useState({
        show: false,
        selectedSearch: null
    });

    // Auto-dismiss the export flash after 10s by triggering the exit animation.
    useEffect(() => {
        if (exportFlash !== 'in') return undefined;
        const timer = setTimeout(() => setExportFlash('out'), 10000);
        return () => clearTimeout(timer);
    }, [exportFlash]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            #product-dropdown::after {
                display: none !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const toggleSidebar = () => {
        setExpanded(!expanded);
    };

    const legacyModeString = exploreByContact ? 'CONTACT' : 'COMPANY';

    // The applied filters live as an entities map on the current universe;
    // SaveModal expects them as an array of filter objects.
    const currentFilters = Object.values(currentUniverseData?.entities ?? {});

    // The cube namespace must match the one the live query uses (prepareFilters)
    // so saved filters parse against the same cube when the search is reloaded.
    const cubeNamespace = resolveUniverseConfig(currentUniverse, exploreByContact)?.cubeNamespace;

    const handleSearchSelect = (selectedSearch) => {
        if (hasFilter) {
            setShowApplySearchConfirmModal({
                show: true,
                selectedSearch: selectedSearch
            });
        } else {
            handleOnSelectSearchCriteria(selectedSearch);
        }
    };

    return (
        <main>
            {/* Sidebar */}
            <Sidebar toggleSidebar={toggleSidebar} expanded={expanded}>
                <FilterSidebar
                    expanded={expanded}
                    toggleSidebar={toggleSidebar}
                />
            </Sidebar>

            <SideBarContent expanded={expanded}>
                {exportFlash !== 'hidden' && (
                    <Alert
                        variant={"success"}
                        className={`export-flash${exportFlash === 'out' ? ' export-flash--out' : ''}`}
                        onClose={() => setExportFlash('out')}
                        onAnimationEnd={() => {
                            if (exportFlash === 'out') setExportFlash('hidden');
                        }}
                        dismissible
                        transition={false}
                    >
                        Your list is being exported. Check at the list page for the progress.
                    </Alert>
                )}
                <Row>
                    <Col xl={12} lg={12} md={12} sm={12}>
                        <Row>
                            <Col xl={12} lg={12} md={12} sm={12}>
                                <ExploreToolbar
                                    // Data
                                    loading={loading}
                                    totalCompanies={totalCompanies}
                                    totalContacts={totalContacts}
                                    hasFilter={hasFilter}
                                    canExport={canExport}
                                    currentUniverse={currentUniverse}
                                    legacyModeString={legacyModeString}
                                    accuracyData={accuracyData}
                                    toggleMap={toggleMap}
                                    exploreByContact={exploreByContact}

                                    // Actions
                                    handleClearAllFilter={handleClearAllFilter}
                                    handleSearch={handleSearch}
                                    onExport={() => setShowExportDialog(true)}
                                    onImport={() => setShowImportDialog(true)}
                                    onSaveSearch={() => setShowSaveSearchDialog(true)}
                                    onSearchSelected={handleSearchSelect}
                                    onAccuracyChange={handleSettingAccuracy}
                                    onToggleMap={onToggleMapChange}
                                />
                            </Col>
                        </Row>

                        {/* --- Dialogs --- */}
                        {showImportDialog && (
                            <UploadListDialog
                                show={showImportDialog}
                                handleClose={() => setShowImportDialog(false)}
                            />
                        )}
                        {showSaveSearchDialog && (
                            <SaveModal
                                title={'Save Search'}
                                show={showSaveSearchDialog}
                                handleClose={() => setShowSaveSearchDialog(false)}
                                exploreMode={{ mode: legacyModeString }}
                                currentFilters={currentFilters}
                                cubeNamespace={cubeNamespace}
                            />
                        )}
                        {showExportDialog && (
                            <ExportModal
                                title={'Export'}
                                show={showExportDialog}
                                handleClose={() => setShowExportDialog(false)}
                                onExportSuccess={() => setExportFlash('in')}
                                exploreMode={{ mode: legacyModeString }}
                            />
                        )}
                        {showApplySearchConfirmModal.show && (
                            <ApplySearchConfirmModel
                                selectedSearch={showApplySearchConfirmModal.selectedSearch}
                                onCancel={() => setShowApplySearchConfirmModal({ show: false })}
                                onSubmit={() => {
                                    setShowApplySearchConfirmModal({ show: false });
                                    handleOnSelectSearchCriteria(showApplySearchConfirmModal.selectedSearch);
                                }}
                            />
                        )}

                        {/* --- Main Content Area --- */}
                        <div>
                            {toggleMap && !exploreByContact ? (
                                <Row className="mt-3">
                                    <Col xl={12}>
                                        <div className="panel shadow-sm" style={{ padding: '0px' }}>
                                            <ExploreMapContainer
                                                exploreMode={legacyModeString}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                <ExploreCards
                                    loading={loading}
                                    mode={legacyModeString}
                                    handleSearch={handleSearch}
                                    expanded={expanded}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            </SideBarContent>
        </main>
    );
};

Explore.propTypes = {
    currentUniverse: PropTypes.string.isRequired,
    handleClearAllFilter: PropTypes.func.isRequired,
    handleSearch: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    hasFilter: PropTypes.bool,
    canExport: PropTypes.bool,
    totalCompanies: PropTypes.any,
    totalContacts: PropTypes.any,
    handleOnSelectSearchCriteria: PropTypes.func,
    handleSettingAccuracy: PropTypes.func,
    // Add other prop types as needed
};

export default Explore;

// --- Helper Component ---
const ApplySearchConfirmModel = ({ selectedSearch, loading, onCancel, onSubmit }) => (
    <Modal
        title={'Load Search'}
        footer={
            <div className={'footer'}>
                <Button variant='primary' onClick={onSubmit}>
                    {loading ? (
                        <div>
                            <Spinner style={{ marginRight: '5px' }} as="span" animation="border" size="sm" />
                            Loading..
                        </div>
                    ) : 'Load'}
                </Button>
                <Button variant='secondary' style={{ marginLeft: '10px' }} onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        }
    >
        <div className="modal-body">
            <div style={{ padding: '50px', color: APP_COLORS.blue }}>
                <p>
                    Are you sure you want to load: <br />
                    <span>
                        {selectedSearch?.display}
                        <span style={{ fontStyle: 'italic', paddingLeft: '10px', color: APP_COLORS['gray-500'] }}>
                            ({moment(selectedSearch?.updated).fromNow()})
                        </span>
                    </span>
                    <br /><br />
                    <span style={{ color: APP_COLORS.yellow }}>
                        Warning: Loading your previous search will undo your active changes.
                    </span>
                </p>
            </div>
        </div>
    </Modal>
);
